/**
 * Theme preference.
 *
 * `system` means "follow the OS", which the token CSS handles via
 * `prefers-color-scheme` guarded by `:root:not([data-theme='light'])`. An explicit
 * choice sets `data-theme` and wins.
 *
 * Stored in localStorage because it is a cosmetic per-viewer preference — the only
 * category of thing allowed in web storage (docs/FRONTEND_ARCHITECTURE.md §5).
 */

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'bemedic.theme';

export function getStoredTheme(): ThemePreference {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') return value;
  } catch {
    // Private mode, blocked storage, or a thrown accessor — fall through to the default.
  }
  return 'system';
}

export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  if (preference === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', preference);
  }
}

export function setTheme(preference: ThemePreference): void {
  applyTheme(preference);
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // A failed write must never break the UI; the theme still applies for this session.
  }
}

/** Call once before first paint to avoid a flash of the wrong theme. */
export function initTheme(): void {
  applyTheme(getStoredTheme());
}
